import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { DatatableColumn } from 'projects/bis-shared/src/lib/datatable/datatable-column';
import { DatatableSort } from 'projects/bis-shared/src/lib/datatable/datatable-sort';
import { DatatableComponent } from 'projects/bis-shared/src/lib/datatable/datatable/datatable.component';
import { DatatableStaticTemplateType } from 'projects/bis-shared/src/lib/datatable/datatable-static-template';
import { AppUrls } from 'projects/bis-shared/src/lib/utility/appurls';
import { ExternalApiUrls } from 'projects/bis-shared/src/lib/utility/apiurls-external';
import { DocumentFormId } from 'projects/bis-shared/src/lib/document-shared/document-form-id.enum';
import { DialogService } from 'projects/bis-shared/src/lib/dialog/dialog.service';
import { PriorAuthRequestService } from '../../webforms/prior-auth-request/prior-auth-request.service';
import { SnackbarService } from 'projects/bis-shared/src/lib/snackbar/snackbar.service';
import { FilingSubmissionStatus } from 'projects/bis-shared/src/lib/utility/filing-constants';
import { FilterDataType } from 'projects/bis-shared/src/lib/datatable/datatable-filter/filter-operations';
import { Router } from '@angular/router';

@Component({
  selector: 'app-draft-eforms-queue',
  templateUrl: './draft-eforms-queue.component.html',
  styleUrls: ['./draft-eforms-queue.component.scss'],
})
export class DraftEformsQueueComponent implements OnInit {
  @ViewChild(DatatableComponent, { static: true }) datatable: DatatableComponent;
  @ViewChild('idTemplate', { static: true }) idTemplate: TemplateRef<any>;
  @ViewChild('associatedWithTemplate', { static: true }) associatedWithTemplate: TemplateRef<any>;

  dtWarningMessage =
    'If you want to resume an existing draft of an eForm, ' +
    "do so from the link in the 'Draft eForm Name' column.";

  columns: DatatableColumn[];
  dataUrl: string;
  defaultSort: DatatableSort[];
  AppUrls = AppUrls;
  FilingSubmissionStatus = FilingSubmissionStatus;

  constructor(
    private externalApiUrls: ExternalApiUrls,
    private dialogService: DialogService,
    private priorAuthRequestService: PriorAuthRequestService,
    private snackbarService: SnackbarService,
    private router: Router,
  ) {
    this.defaultSort = [
      {
        property: 'updateDate',
        sortAscending: false,
      },
      {
        property: 'relatedObjectTypeCode',
        sortAscending: true,
      },
      {
        property: 'relatedObjectId',
        sortAscending: true,
      },
    ];
  }

  ngOnInit(): void {
    this.columns = this.getDatatableColumns();
    this.dataUrl = this.externalApiUrls.filing.searchDraftEforms();
    this.datatable.emptyMessage = 'There are no draft eForms to display.';
  }

  getDatatableColumns(): DatatableColumn[] {
    return [
      {
        title: 'Draft eForm Name',
        property: 'formType',
        filterDataType: FilterDataType.String,
        template: this.idTemplate,
        width: 400,
      },
      {
        title: 'Patient Name',
        property: 'patient',
        filterDataType: FilterDataType.String,
      },
      {
        title: 'Patient DOB',
        property: 'dateOfBirth',
        filterDataType: FilterDataType.Date,
        template: DatatableStaticTemplateType.UtcDateTemplate,
      },
      {
        title: 'WCB Case #',
        property: 'wcbCaseNumber',
        filterDataType: FilterDataType.String,
        template: DatatableStaticTemplateType.CaseNumberTemplate,
      },
      {
        title: 'eForm Details',
        property: 'relatedObjectIdFormatted',
        filterDataType: FilterDataType.SystemId,
        filterSystemIdColumnNames: { typeCode: 'relatedObjectTypeCode', id: 'relatedObjectId' },
        template: this.associatedWithTemplate,
      },
      {
        title: 'For',
        property: 'submitOnBehalfOfEntityName',
        filterDataType: FilterDataType.String,
      },
      {
        title: 'Status',
        property: 'filingSubmissionStatus',
        filterDataType: FilterDataType.Reference,
      },
      { title: 'Last Updated By', property: 'updateUser', filterDataType: FilterDataType.String },
      {
        title: 'Last Updated Date',
        property: 'updateDate',
        filterDataType: FilterDataType.DateTime,
        template: DatatableStaticTemplateType.EasternDateTemplate,
      },
    ];
  }

  getDraftEformLink(row) {
    switch (row.documentFormId) {
      case DocumentFormId.MedicalBillingDispute:
        return [AppUrls.filings.medicalBillingDispute.edit(row.filingSubmissionId)];
      case DocumentFormId.ConfirmationL1:
        return [AppUrls.filings.priorAuthRequest.editConfirmation(row.filingSubmissionId)];
      case DocumentFormId.MedicationL1:
        return [AppUrls.filings.priorAuthRequest.editMedication(row.filingSubmissionId)];
      case DocumentFormId.DurableMedicalEquipmentL1:
        return [
          AppUrls.filings.priorAuthRequest.editDurableMedicalEquipment(row.filingSubmissionId),
        ];
      case DocumentFormId.NonMtgOver1000L1:
        return [AppUrls.filings.priorAuthRequest.editNonMtgOver1000(row.filingSubmissionId)];
      case DocumentFormId.NonMtgUnder1000L1:
        return [AppUrls.filings.priorAuthRequest.editNonMtgUnder1000(row.filingSubmissionId)];
      case DocumentFormId.SpecialServicesL1:
        return [AppUrls.filings.priorAuthRequest.editSpecialServices(row.filingSubmissionId)];
      case DocumentFormId.VarianceL1:
        return [AppUrls.filings.priorAuthRequest.editVariance(row.filingSubmissionId)];
      case DocumentFormId.MedicationL2:
        return [
          AppUrls.priorAuthorization.resumeL2Review(row.relatedObjectId, row.filingSubmissionId),
        ];
      case DocumentFormId.ConfirmationL1ResponseDraft:
      case DocumentFormId.ConfirmationL2ResponseDraft:
      case DocumentFormId.DurableMedicalEquipmentL1ResponseDraft:
      case DocumentFormId.DurableMedicalEquipmentL2ResponseDraft:
      case DocumentFormId.MedicationL1ResponseDraft:
      case DocumentFormId.MedicationL2ResponseDraft:
      case DocumentFormId.NonMtgOver1000L1ResponseDraft:
      case DocumentFormId.NonMtgOver1000L2ResponseDraft:
      case DocumentFormId.NonMtgUnder1000L1ResponseDraft:
      case DocumentFormId.NonMtgUnder1000L2ResponseDraft:
      case DocumentFormId.SpecialServicesL1ResponseDraft:
      case DocumentFormId.SpecialServicesL2ResponseDraft:
      case DocumentFormId.VarianceL1ResponseDraft:
      case DocumentFormId.VarianceL2ResponseDraft:
      case DocumentFormId.ConfirmationReviewGadDraft:
      case DocumentFormId.VarianceReviewGadDraft:
      case DocumentFormId.SpecialServicesReviewGadDraft:
      case DocumentFormId.NonMtgOver1000ReviewGadDraft:
      case DocumentFormId.NonMtgUnder1000ReviewGadDraft:
      case DocumentFormId.DurableMedicalEquipmentReviewGadDraft:
      case DocumentFormId.ConfirmationResponseDenied:
      case DocumentFormId.ConfirmationResponseGranted:
      case DocumentFormId.ConfirmationResponseGrantedInPart:
      case DocumentFormId.DurableMedicalEquipmentResponseDenied:
      case DocumentFormId.DurableMedicalEquipmentResponseGranted:
      case DocumentFormId.DurableMedicalEquipmentResponseGrantedInPart:
      case DocumentFormId.MedicationL1ResponseDenied:
      case DocumentFormId.MedicationL1ResponseGranted:
      case DocumentFormId.MedicationL1ResponseGrantedInPart:
      case DocumentFormId.MedicationL2ResponseDenied:
      case DocumentFormId.MedicationL2ResponseGranted:
      case DocumentFormId.MedicationL2ResponseGrantedInPart:
      case DocumentFormId.NonMtgOver1000ResponseDenied:
      case DocumentFormId.NonMtgOver1000ResponseGranted:
      case DocumentFormId.NonMtgOver1000ResponseGrantedInPart:
      case DocumentFormId.NonMtgUnder1000ResponseDenied:
      case DocumentFormId.NonMtgUnder1000ResponseGranted:
      case DocumentFormId.NonMtgUnder1000ResponseGrantedInPart:
      case DocumentFormId.SpecialServicesResponseDenied:
      case DocumentFormId.SpecialServicesResponseGranted:
      case DocumentFormId.SpecialServicesResponseGrantedInPart:
      case DocumentFormId.VarianceResponseDenied:
      case DocumentFormId.VarianceResponseGranted:
      case DocumentFormId.VarianceResponseGrantedInPart:
        return [
          AppUrls.priorAuthorization.editDraftRequest(row.relatedObjectId, row.filingSubmissionId),
        ];
      default:
        console.warn(`Unknown eForm Type ${row.formType}, ID: ${row.documentFormId}`);
    }
  }

  onDiscard(row) {
    this.dialogService.showDiscardConfirmation().subscribe(discard => {
      if (discard) {
        this.priorAuthRequestService.discardDraftEForm(row.filingSubmissionId).subscribe(() => {
          this.snackbarService.openSnackBarSuccess('Draft Form Discarded Successfully');
          this.datatable.reloadTable();
        });
      }
    });
  }
}
